/**
 * Sidebar organism — slide-in panel. Portal, backdrop, ESC, scroll lock.
 * Optional header: icon, title, close button. Configurable side and width.
 */

import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Typography } from "../../atoms/Typography";
import { IconWrapper } from "../../atoms/IconWrapper";
import { Button } from "../../atoms/Button";
import {
  getBackdropClasses,
  getPanelClasses,
  getHeaderClasses,
  getIconBadgeClasses,
} from "./Sidebar.styles";
import type { SidebarProps } from "./Sidebar.types";

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  children,
  width = "lg",
  ariaLabel = "Side panel",
  side = "right",
  headerTitle,
  headerIcon,
  showHeaderIcon = true,
  showCloseButton = true,
}) => {
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const content = (
    <>
      <div
        className={getBackdropClasses(isOpen)}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />
      <div
        className={getPanelClasses(side, width, isOpen)}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
      >
        <div className="flex flex-col h-full">
          {headerTitle && (
            <div className={getHeaderClasses()}>
              <div className="flex items-center gap-2">
                {showHeaderIcon && headerIcon && (
                  <div className={getIconBadgeClasses()}>
                    <IconWrapper size="sm" color="white" ariaHidden>
                      {headerIcon}
                    </IconWrapper>
                  </div>
                )}
                <Typography as="span" variant="h3" color="primary" weight="bold">
                  {headerTitle}
                </Typography>
              </div>
              {showCloseButton && (
                <Button
                  type="icon"
                  intent="secondary"
                  size="sm"
                  onClick={onClose}
                  aria-label="Close"
                  icon={
                    <IconWrapper size="sm" ariaHidden>
                      <X />
                    </IconWrapper>
                  }
                />
              )}
            </div>
          )}
          <div className="flex-1 min-h-0 flex flex-col">
            {children}
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};
